import { midi } from '../midi/MidiBus';
import { noteOff, noteOn } from '../midi/generate';
import { useKeyboardStore } from '../store/keyboardStore';
import { panelActions } from '../store/panelStore';
import { MIDI_CHANNEL } from '../audio/transport';
import {
  COMPUTER_KEYBOARD_BASE_NOTE,
  NOTE_KEYS,
  OCTAVE_KEYS,
  PANEL_KEY_MAP,
  WHEEL_COMMANDS,
} from './keymap';

const WHEEL_REPEAT_MS = 20;

/**
 * Binds the computer keyboard and the scroll wheel.
 *
 * Panel keys fire on key-down and latch until key-up so a held key cannot
 * retrigger; note keys mint MIDI onto the shared bus, exactly like hardware.
 */
export function installInput(): () => void {
  const heldPanelKeys = new Set<string>();
  const heldNoteKeys = new Map<string, number>();

  const onKeyDown = (event: KeyboardEvent) => {
    // The panel keeps a history of every command, so undo is free.
    if ((event.metaKey || event.ctrlKey) && event.code === 'KeyZ') {
      event.preventDefault();
      panelActions.undo();
      return;
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    // Never steal keys from a focused input.
    const target = event.target as HTMLElement | null;
    if (target?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName ?? '')) return;

    const command = PANEL_KEY_MAP.get(event.code);
    if (command) {
      event.preventDefault();
      if (!heldPanelKeys.has(event.code)) {
        heldPanelKeys.add(event.code);
        panelActions.press(command);
        panelActions.run(command);
      }
      return;
    }

    if (event.code === OCTAVE_KEYS.DOWN || event.code === OCTAVE_KEYS.UP) {
      event.preventDefault();
      useKeyboardStore.getState().shiftOctave(event.code === OCTAVE_KEYS.UP ? 1 : -1);
      return;
    }

    const semitone = NOTE_KEYS.get(event.code);
    if (semitone === undefined || heldNoteKeys.has(event.code)) return;
    event.preventDefault();
    const { octave } = useKeyboardStore.getState();
    const note = COMPUTER_KEYBOARD_BASE_NOTE + semitone + octave * 12;
    heldNoteKeys.set(event.code, note);
    midi.send(noteOn(note, 100, MIDI_CHANNEL, 'keyboard'));
  };

  const onKeyUp = (event: KeyboardEvent) => {
    const command = PANEL_KEY_MAP.get(event.code);
    if (command && heldPanelKeys.delete(event.code)) {
      panelActions.release(command);
      return;
    }
    const note = heldNoteKeys.get(event.code);
    if (note !== undefined) {
      heldNoteKeys.delete(event.code);
      midi.send(noteOff(note, 0, MIDI_CHANNEL, 'keyboard'));
    }
  };

  // A window blur would otherwise leave keys latched and notes ringing.
  const onBlur = () => {
    for (const code of heldPanelKeys) {
      const command = PANEL_KEY_MAP.get(code);
      if (command) panelActions.release(command);
    }
    heldPanelKeys.clear();
    for (const note of heldNoteKeys.values()) {
      midi.send(noteOff(note, 0, MIDI_CHANNEL, 'keyboard'));
    }
    heldNoteKeys.clear();
  };

  const onWheel = (event: WheelEvent) => {
    event.preventDefault();
    const command = event.deltaY > 0 ? WHEEL_COMMANDS.UP : WHEEL_COMMANDS.DOWN;
    panelActions.press(command);
    panelActions.run(command);
    setTimeout(() => panelActions.release(command), WHEEL_REPEAT_MS);
  };

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', onBlur);
  window.addEventListener('wheel', onWheel, { passive: false });

  return () => {
    onBlur();
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('wheel', onWheel);
  };
}
